import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Activity, Database, Server, Mail, RefreshCw, Loader2, Bug, Calendar, HardDrive,
  CheckCircle2, XCircle, AlertTriangle, Cpu, Bell, ListChecks, ShieldCheck, Zap, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Tiny presentational helpers ────────────────────────────────────────
function Dot({ ok }) {
  return <span className={`inline-block w-2 h-2 rounded-full ${ok ? 'bg-emerald-500' : 'bg-rose-500'}`} />;
}
function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
function fmtDelta(d) {
  if (d == null) return '—';
  if (Number.isInteger(d)) return d.toLocaleString();
  return Number(d).toLocaleString(undefined, { maximumFractionDigits: 4 });
}
function StatPill({ tone = 'default', label, value }) {
  const toneMap = {
    default: 'bg-muted text-foreground border-border',
    good:    'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    warn:    'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
    bad:     'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30',
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneMap[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
    </div>
  );
}

// ─── Health probe row ───────────────────────────────────────────────────
const PROBE_ICONS = {
  mongo: Database,
  scheduler: Calendar,
  email: Mail,
  llm: Cpu,
  playwright: Activity,
  env: ShieldCheck,
  jobs: ListChecks,
  disk: HardDrive,
  sentry: Bug,
};
function ProbeRow({ name, probe }) {
  const Icon = PROBE_ICONS[name] || Server;
  const ok = probe?.ok;
  const metrics = Object.entries(probe || {})
    .filter(([k]) => !['ok', 'error', 'reason', 'detail', 'fix_path', 'fix_label'].includes(k))
    .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`)
    .join(' · ');
  const reason = probe?.reason || probe?.detail || probe?.error || '';
  const fixPath = probe?.fix_path;
  const fixLabel = probe?.fix_label || 'Fix this';
  return (
    <div className="flex items-start justify-between gap-3 border rounded-lg px-3 py-2.5 bg-card" data-testid={`status-probe-${name}`}>
      <div className="flex items-center gap-3 shrink-0 pt-0.5">
        <Icon className={`w-4 h-4 ${ok ? 'text-emerald-500' : 'text-rose-500'}`} />
        <div className="font-medium capitalize">{name}</div>
        <Dot ok={ok} />
      </div>
      {ok ? (
        <div className="text-xs text-muted-foreground font-mono text-right">
          {metrics || probe?.detail || '—'}
        </div>
      ) : (
        <div className="text-xs text-rose-500 text-right max-w-[62%]" data-testid={`status-reason-${name}`}>
          {fixPath ? (
            <Link
              to={fixPath}
              className="group inline-block hover:text-rose-400"
              data-testid={`status-fix-link-${name}`}
            >
              <span className="underline decoration-dotted underline-offset-2">
                {reason || 'Not available — no diagnostic reported.'}
              </span>
              <span className="mt-0.5 flex items-center justify-end gap-1 text-[11px] font-semibold text-rose-600 dark:text-rose-400 group-hover:underline">
                {fixLabel}
                <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          ) : (
            reason || 'Not available — no diagnostic reported.'
          )}
          {metrics ? <span className="block text-muted-foreground font-mono mt-0.5">{metrics}</span> : null}
        </div>
      )}
    </div>
  );
}

// ─── Recent bursts table ────────────────────────────────────────────────
function BurstsTable({ rows }) {
  if (!rows?.length) {
    return <div className="text-sm text-muted-foreground italic">No recent scheduled bursts.</div>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="text-left px-3 py-2">Job</th>
            <th className="text-left px-3 py-2">Tenant</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Attempts</th>
            <th className="text-left px-3 py-2">When</th>
            <th className="text-left px-3 py-2">Error</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.job_id} className="border-t">
              <td className="px-3 py-2 font-mono text-xs">{b.job_id}</td>
              <td className="px-3 py-2">{b.tenant_id || '—'}</td>
              <td className="px-3 py-2">
                <Badge variant={b.status === 'completed' ? 'default' : b.status === 'failed' ? 'destructive' : 'secondary'}>
                  {b.status}
                </Badge>
              </td>
              <td className="px-3 py-2">{b.attempts}</td>
              <td className="px-3 py-2 text-xs text-muted-foreground">{fmtTime(b.updated_at || b.created_at)}</td>
              <td className="px-3 py-2 text-xs text-rose-500 max-w-md truncate">{b.last_error || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Sanity Check Result row ────────────────────────────────────────────
const STATUS_TONE = {
  ok: { icon: CheckCircle2, klass: 'text-emerald-500' },
  mismatch: { icon: AlertTriangle, klass: 'text-rose-500' },
  no_data: { icon: XCircle, klass: 'text-muted-foreground' },
  incomparable: { icon: AlertTriangle, klass: 'text-amber-500' },
  unknown: { icon: AlertTriangle, klass: 'text-muted-foreground' },
};
function SanityTable({ result }) {
  if (!result) return null;
  const checks = result.checks || [];
  if (!checks.length) {
    return <div className="text-sm text-muted-foreground italic">No measures found to verify in this tenant.</div>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="text-left px-3 py-2">Model</th>
            <th className="text-left px-3 py-2">Entity</th>
            <th className="text-left px-3 py-2">Field</th>
            <th className="text-left px-3 py-2">Agg</th>
            <th className="text-right px-3 py-2">Expected (raw)</th>
            <th className="text-right px-3 py-2">Actual (engine)</th>
            <th className="text-right px-3 py-2">Δ %</th>
            <th className="text-left px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {checks.map((c, i) => {
            const t = STATUS_TONE[c.status] || STATUS_TONE.unknown;
            const Icon = t.icon;
            return (
              <tr key={i} className="border-t" data-testid={`sanity-row-${i}`}>
                <td className="px-3 py-2 truncate max-w-[140px]" title={c.model_name}>{c.model_name}</td>
                <td className="px-3 py-2">{c.entity}</td>
                <td className="px-3 py-2 font-mono text-xs">{c.field}</td>
                <td className="px-3 py-2 uppercase text-xs">{c.aggregation}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{fmtDelta(c.expected)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{fmtDelta(c.actual)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {c.delta_pct == null ? '—' : `${c.delta_pct}%`}
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center gap-1.5 ${t.klass}`}>
                    <Icon className="w-3.5 h-3.5" />
                    <span className="text-xs capitalize">{c.status.replace('_', ' ')}</span>
                  </span>
                  {c.error && <div className="text-[10px] text-rose-400 mt-0.5 max-w-xs truncate" title={c.error}>{c.error}</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────
export default function StatusPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sanityResult, setSanityResult] = useState(null);
  const [sanityLoading, setSanityLoading] = useState(false);
  const [perf, setPerf] = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { ok, data } = await apiFetch(`${API}/api/admin/status`, { method: 'GET' });
      if (!ok) throw new Error(data?.detail || 'Failed to load status');
      setStatus(data);
    } catch (e) {
      toast.error(`Status load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const runSanity = useCallback(async () => {
    setSanityLoading(true);
    setSanityResult(null);
    try {
      const { ok, data } = await apiFetch(`${API}/api/admin/sanity-checks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sample_limit: 25 }),
      });
      if (!ok) throw new Error(data?.detail || 'Sanity checks failed');
      setSanityResult(data);
      const { mismatch } = data.summary;
      if (mismatch > 0) toast.warning(`${mismatch} measure mismatch(es) detected`);
      else toast.success(`All ${data.summary.ok} measures verified clean.`);
    } catch (e) {
      toast.error(`Sanity check failed: ${e.message}`);
    } finally {
      setSanityLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── GAP-4: pull query performance telemetry on mount + on demand ──
  const loadPerf = useCallback(async () => {
    setPerfLoading(true);
    try {
      const { ok, data } = await apiFetch(`${API}/api/admin/query-performance`, { method: 'GET' });
      if (!ok) throw new Error(data?.detail || 'Failed to load query performance');
      setPerf(data);
    } catch (e) {
      toast.error(`Performance load failed: ${e.message}`);
    } finally {
      setPerfLoading(false);
    }
  }, []);
  useEffect(() => { loadPerf(); }, [loadPerf]);

  const overall = status?.status;
  const probes = status?.health || {};
  const summary = status?.summary || {};
  const lastReg = status?.last_regression;
  const recentBursts = status?.recent_bursts || [];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="status-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Activity className="w-7 h-7 text-primary" />
            Platform Status
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live dependency probes, recent scheduled bursts, last CI regression, and data-correctness checks.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {overall && (
            <Badge
              variant={overall === 'ok' ? 'default' : 'destructive'}
              className="text-sm py-1 px-3"
              data-testid="status-overall"
            >
              <Dot ok={overall === 'ok'} />
              <span className="ml-1.5">{overall === 'ok' ? 'All systems operational' : 'Degraded'}</span>
            </Badge>
          )}
          <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="status-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatPill label="Tenants" value={summary.tenants ?? '—'} />
        <StatPill label="Users" value={summary.users ?? '—'} />
        <StatPill label="Active Schedules" value={summary.active_schedules ?? '—'} />
        <StatPill
          label="Recent Burst Failures"
          value={summary.recent_burst_failures ?? '—'}
          tone={summary.recent_burst_failures > 0 ? 'bad' : 'good'}
        />
      </div>

      {/* Health probes */}
      <section className="space-y-3" data-testid="status-health-section">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Bell className="w-4 h-4 text-primary" />
          Dependency Probes
        </h2>
        {loading && !status ? (
          <div className="text-sm text-muted-foreground italic">Probing dependencies…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Object.entries(probes).map(([name, probe]) => (
              <ProbeRow key={name} name={name} probe={probe} />
            ))}
          </div>
        )}
      </section>

      {/* Last regression */}
      <section className="space-y-3" data-testid="status-regression-section">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-primary" />
          Last CI Regression Run
        </h2>
        {lastReg ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatPill label="Suite" value={lastReg.suite || '—'} />
            <StatPill label="Environment" value={lastReg.environment || '—'} />
            <StatPill label="Passed" value={lastReg.passed ?? 0} tone="good" />
            <StatPill
              label="Failed"
              value={lastReg.failed ?? 0}
              tone={(lastReg.failed ?? 0) > 0 ? 'bad' : 'good'}
            />
            <StatPill label="When" value={fmtTime(lastReg.created_at)} />
          </div>
        ) : (
          <div className="text-sm text-muted-foreground italic">
            No regression runs reported yet. CI will POST to <code className="font-mono">/api/admin/status/regression-log</code> after each suite.
          </div>
        )}
      </section>

      {/* Recent bursts */}
      <section className="space-y-3" data-testid="status-bursts-section">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Calendar className="w-4 h-4 text-primary" />
          Recent Scheduled Bursts (last 20)
        </h2>
        <BurstsTable rows={recentBursts} />
      </section>

      {/* Sanity checks */}
      <section className="space-y-3" data-testid="status-sanity-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-primary" />
            Data Correctness Sanity Checks
          </h2>
          <Button
            onClick={runSanity}
            disabled={sanityLoading}
            size="sm"
            data-testid="run-sanity-checks-btn"
          >
            {sanityLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListChecks className="w-4 h-4" />}
            {sanityLoading ? 'Running…' : 'Run sanity checks'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Cross-verifies each semantic measure against a direct MongoDB aggregate. A non-zero delta is a silent correctness bug.
        </p>
        {sanityResult && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatPill label="Checked" value={sanityResult.summary?.total ?? 0} />
            <StatPill label="OK" value={sanityResult.summary?.ok ?? 0} tone="good" />
            <StatPill
              label="Mismatches"
              value={sanityResult.summary?.mismatch ?? 0}
              tone={(sanityResult.summary?.mismatch ?? 0) > 0 ? 'bad' : 'good'}
            />
            <StatPill label="Errors" value={sanityResult.summary?.errors ?? 0} tone={(sanityResult.summary?.errors ?? 0) > 0 ? 'warn' : 'default'} />
          </div>
        )}
        <SanityTable result={sanityResult} />
      </section>

      {/* ── GAP-4: Query performance telemetry ── */}
      <section className="space-y-3" data-testid="status-perf-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary" />
            Query Performance (last {perf?.total_queries ?? 0} queries)
          </h2>
          <Button onClick={loadPerf} disabled={perfLoading} size="sm" variant="outline" data-testid="perf-refresh-btn">
            {perfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Materialized-view hit rate &amp; sampling rate prove the query engine is keeping aggregation work pushed down to MongoDB (not streaming rows into Python). Slow queries (&gt;1.5s) are surfaced for investigation.
        </p>
        {perf && perf.total_queries > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatPill label="Avg ms"          value={perf.avg_duration_ms} />
              <StatPill label="P95 ms"          value={perf.p95_duration_ms} tone={perf.p95_duration_ms > 2000 ? 'warn' : 'default'} />
              <StatPill label="Materialized %"  value={`${perf.materialized_hit_rate}%`} tone={perf.materialized_hit_rate > 30 ? 'good' : 'default'} />
              <StatPill label="Sampled %"       value={`${perf.sampled_rate}%`} />
              <StatPill label="Error rate"      value={`${perf.error_rate}%`} tone={perf.error_rate > 0 ? 'bad' : 'good'} />
            </div>
            {perf.slow_queries?.length > 0 && (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 overflow-hidden" data-testid="slow-queries-table">
                <div className="px-3 py-2 text-xs font-semibold text-amber-300 border-b border-amber-500/20 flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5" /> Slow queries ({perf.slow_queries.length})
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-amber-500/5">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="px-3 py-1.5">Collection</th>
                      <th className="px-3 py-1.5 text-right">Duration</th>
                      <th className="px-3 py-1.5 text-right">Rows</th>
                      <th className="px-3 py-1.5">Stages</th>
                      <th className="px-3 py-1.5">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perf.slow_queries.slice(0, 10).map((q, i) => (
                      <tr key={i} className="border-t border-amber-500/10">
                        <td className="px-3 py-1.5 font-mono text-[10px]">{q.collection}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-amber-300">{q.duration_ms} ms</td>
                        <td className="px-3 py-1.5 text-right text-muted-foreground">{q.rows_returned}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{q.pipeline_stages}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{fmtTime(q.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {perf.top_collections?.length > 0 && (
              <div className="rounded-lg border border-border/30 overflow-hidden">
                <div className="px-3 py-2 text-xs font-semibold border-b border-border/30">Top collections by query volume</div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="px-3 py-1.5">Collection</th>
                      <th className="px-3 py-1.5 text-right">Queries</th>
                      <th className="px-3 py-1.5 text-right">Avg ms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perf.top_collections.map((c, i) => (
                      <tr key={i} className="border-t border-border/20">
                        <td className="px-3 py-1.5 font-mono text-[10px]">{c.collection}</td>
                        <td className="px-3 py-1.5 text-right">{c.count}</td>
                        <td className="px-3 py-1.5 text-right text-muted-foreground">{c.avg_ms} ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-muted-foreground italic">No telemetry yet — run a few queries and refresh.</div>
        )}
      </section>

      {status?.generated_at && (
        <div className="text-xs text-muted-foreground pt-4 border-t">
          Generated at <span className="font-mono">{fmtTime(status.generated_at)}</span>. Tolerance for mismatch detection: <span className="font-mono">{((sanityResult?.tolerance_pct ?? 0.01))}%</span>.
        </div>
      )}
    </div>
  );
}
